/**
 * Home state views — loading / empty / error.
 *
 * Phase 3.1 — Foundation. Three sub-screens for the home route's
 * non-ready states. Each renders inside the same SafeAreaView frame
 * so layout doesn't shift when state transitions.
 *
 * - LoadingView:   first GET /v1/child + GET /v1/daily/:child_id in flight
 * - EmptyView:     daily offer returns 0 cards (pool exhausted)
 * - ErrorView:     network or server error fetching daily offer
 *
 * "needs_onboarding" (no child profile) is handled inline in home.tsx
 * with router.replace — there's no UI to show, we just bounce.
 */

import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { AppIcon } from '@/components/app-icon';
import { Brand, Spacing } from '@/constants/theme';
import { temaIcon } from '@/lib/icons';
import type { ChildProfile } from '@funpaper/shared';

export function LoadingView() {
  return (
    <View style={styles.center}>
      <ActivityIndicator size="large" color={Brand.primary} />
      <ThemedText type="small" themeColor="textSecondary" style={styles.text}>
        Menyiapkan worksheet hari ini…
      </ThemedText>
    </View>
  );
}

export function EmptyView({ child, message }: { child: ChildProfile; message: string }) {
  return (
    <View style={styles.center}>
      <AppIcon source={temaIcon('Tanaman')} size={72} />
      <ThemedText type="subtitle" style={styles.title}>
        Hai {child.name}!
      </ThemedText>
      <ThemedText type="default" themeColor="textSecondary" style={styles.body}>
        {message}
      </ThemedText>
    </View>
  );
}

export function ErrorView({ message }: { message: string }) {
  return (
    <View style={styles.center}>
      <ThemedText type="subtitle" style={styles.title}>
        Gagal memuat
      </ThemedText>
      <ThemedText type="default" themeColor="textSecondary" style={styles.body}>
        {message}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: Spacing.four,
    gap: Spacing.three,
  },
  title: {
    textAlign: 'center',
    color: Brand.primary,
  },
  body: {
    textAlign: 'center',
    maxWidth: 320,
    lineHeight: 22,
  },
  text: {
    marginTop: Spacing.two,
  },
});
